// Maps raw Firebase Auth and callable error codes to user-friendly messages so
// we never surface strings like "Firebase: Error (auth/invalid-credential)." or
// a bare "internal" to end users.

import { REASON_COPY, selfServeReason } from './selfServe';

// A first-time Google sign-in while Identity Platform sign-up is closed, or a
// sign-in with no SolushipX account behind it. Accounts are created from the
// sign-up page (or by an administrator's invitation).
export const NO_ACCOUNT_MESSAGE = 'No SolushipX account exists for this email yet. Create one on the sign-up page, or ask your administrator for an invitation.';

// A password login whose Auth account has no profile: an account that was
// started but never finished. Requesting an activation link with the same
// email finishes it.
export const FINISH_ACCOUNT_MESSAGE = 'Finish creating your account: request an activation link with this email on the sign-up page.';

// A profile that exists but is attached to no company (or was set up by an
// administrator and not finished): only staff can fix it.
export const NOT_PROVISIONED_MESSAGE = 'Your SolushipX account is not set up yet. Please contact your administrator.';

export const DEACTIVATED_MESSAGE = 'Your account has been deactivated. Please contact your administrator.';

const PASSWORD_RULE_MESSAGE = 'Please choose a stronger password: at least 8 characters and not a common password.';

const AUTH_ERROR_MESSAGES = {
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/missing-email': 'Please enter your email address.',
    'auth/user-disabled': DEACTIVATED_MESSAGE,
    'auth/user-not-found': 'Incorrect email or password.',
    'auth/wrong-password': 'Incorrect email or password.',
    'auth/invalid-credential': 'Incorrect email or password.',
    'auth/invalid-login-credentials': 'Incorrect email or password.',
    'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
    'auth/network-request-failed': 'Network error. Check your connection and try again.',
    'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled.',
    'auth/account-exists-with-different-credential': 'You already have an account with this email. Sign in the way you did before (email and password, Google or Microsoft), or request an activation link on the sign-up page.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact your administrator.',
    'auth/missing-password': 'Please enter your password.',
    // Identity Platform sign-up is closed, so a first-time Google sign-in (no
    // Auth account yet) is refused with this code.
    'auth/admin-restricted-operation': NO_ACCOUNT_MESSAGE,
    'auth/email-already-in-use': 'An account already exists for this email. Please sign in instead.',
    'auth/weak-password': PASSWORD_RULE_MESSAGE,
    'auth/password-does-not-meet-requirements': PASSWORD_RULE_MESSAGE
};

// Callable (functions/*) errors without a self-serve reason code. The server's
// own message is not shown for these: for 'internal' it is just "internal".
const FUNCTIONS_ERROR_MESSAGES = {
    // Missing or rejected App Check token, or an expired sign-in.
    'functions/unauthenticated': 'We could not verify this browser. Please reload the page and try again.',
    'functions/resource-exhausted': 'Too many attempts. Please wait a few minutes and try again.',
    'functions/unavailable': 'The service is temporarily unavailable. Please try again in a few minutes.',
    'functions/deadline-exceeded': 'That took too long. Please try again.',
    'functions/permission-denied': 'This action is not allowed for your account.',
    'functions/invalid-argument': 'Some details are missing or invalid. Please check the form and try again.'
};

// A callable error message the server wrote for the user, not just its code
// word ("internal", "Unauthenticated").
const isServerWrittenMessage = (message, code) => {
    const text = typeof message === 'string' ? message.trim().toLowerCase() : '';
    const word = code.slice('functions/'.length).toLowerCase();
    return text !== '' && text !== word && text !== word.replace(/-/g, ' ')
        && !/^(internal|unavailable|deadline[- ]exceeded)$/.test(text);
};

/**
 * Convert a Firebase Auth or callable error (or any thrown error) into a
 * friendly message.
 * @param {*} err - The caught error (Firebase error, Error, or string)
 * @param {string} fallback - Message to use when the code is unknown
 * @param {{ serverMessages?: boolean }} [options] - serverMessages: show a
 *   callable's own message (one without a self-serve reason) instead of the
 *   generic copy for its code. Off for the sign-in / sign-up pages.
 * @returns {string}
 */
export const friendlyAuthError = (err, fallback = 'Something went wrong. Please try again.', { serverMessages = false } = {}) => {
    const code = err?.code;
    if (code && AUTH_ERROR_MESSAGES[code]) {
        return AUTH_ERROR_MESSAGES[code];
    }
    if (typeof code === 'string' && code.startsWith('functions/')) {
        const reason = selfServeReason(err);
        if (reason) return REASON_COPY[reason].message;
        // Screens whose callables write user-facing messages (Carrier Accounts)
        // opt in to show them; a bare code word ("internal", "unavailable")
        // never is.
        if (serverMessages && isServerWrittenMessage(err.message, code)) return err.message;
        return FUNCTIONS_ERROR_MESSAGES[code] || fallback;
    }
    // App-thrown errors (e.g. deactivated-account guard) carry a clean message
    // without a Firebase code — pass those through as-is.
    if (err?.message && !/^Firebase:/i.test(err.message)) {
        return err.message;
    }
    return fallback;
};

export default friendlyAuthError;
